/**
 * The leasing seat's surface — one queue, and the unit pages it opens.
 *
 * The sibling of `accountingSurface.ts`, built to the same shape (a nav-key
 * set, a seat predicate, an allow-list) for the same reason: a department that
 * only ever does one job should not be handed the whole product's navigation.
 *
 * ★ WHY THE PAGE EXISTS AT ALL (PO, 2026-08-12). A "Unit rent-ready — begin
 * marketing" task used to deep-link straight to the GREEN_LIGHT inspection —
 * the property manager's surface, full of controls leasing may not use, and
 * carrying nothing about the apartment. *"So the leasing team doesn't know what
 * to do and has no details about the task itself."* `/leasing/turns/:id` is
 * their own read of the same turn: is it rentable, what do I do next, what are
 * the facts I quote a prospect.
 *
 * ★ THE SEAT IS A DEPARTMENT, NOT A ROLE — and that is the one structural
 * difference from accounting. `OrgRole` has an `ACCOUNTING` member; it has no
 * `LEASING` one, and minting it is a migration nobody has approved. So the gate
 * reads `OrgUser.primaryDepartment`, which already exists on the session and
 * already drives which handoffs land in this person's queue. It is isolated in
 * `isLeasingSeat` precisely so the day a LEASING role appears, exactly one
 * function changes.
 *
 * ★ ORG_ADMIN IS EXCLUDED even when their department says Leasing. An admin
 * administers the whole org; trimming their sidebar to a single entry because
 * of a department field would lock them out of the settings only they can
 * reach. Same instinct as accounting's ops bypass — never trap the operator.
 *
 * ★ IT LINKS OUT, AND ACCOUNTING DELIBERATELY DOES NOT. `isAccountingAllowedPath`
 * was NARROWED to drop `/work-orders/:id` and `/inspections/:id/*` (PO,
 * 2026-08-04: *"Accounting should not review work order / whole turn /
 * inspection."*). Leasing is the opposite ruling on the same question (PO,
 * 2026-08-12: *"Yes, we can link to inspection. If they need, they can see
 * inspection. There are photos and everything there."*), because the evidence
 * IS the answer to leasing's question — what condition is the unit in, what was
 * fixed — and no amount of copying it onto the task page replaces the photos.
 * So both are admitted below, read-only detail paths only: no `/work-orders`
 * register, no `/inspections` register, no `/edit`, no `/create-work-order`.
 */

const UUID =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

/** Sidebar keys the leasing seat may see — their queue, and nothing else. */
export const LEASING_NAV_KEYS = new Set(['/my-queue']);

/**
 * The shape this gate reads off the session. Structural rather than the
 * `AppUser` union so an ops user (no `primaryDepartment` at all) is a plain
 * `false` instead of a cast.
 */
export interface LeasingSeatUser {
  type?: string;
  role?: string;
  primaryDepartment?: string | null;
}

/**
 * TRUE for a person whose whole job here is the leasing queue.
 *
 * ★ THE DEPARTMENT DECIDES. THE ROLE IS IRRELEVANT. This is a direct PO ruling
 * (2026-08-12), given twice: *"you show leasing by department. So if someone
 * doesn't matter about their role, is attached to department leasing, then he
 * sees only leasing view — My Queue and leasing tasks."*
 *
 * It differs from the accounting seat next door, which keys on a ROLE
 * (`canSeeAccountingBillsPage` → `role === 'ACCOUNTING'`, bidirectionally locked
 * to its department by `shared/accounting-role-lock.ts`). There is no LEASING
 * role in `UserRole`, and rather than mint one behind a migration the PO chose
 * the department as the signal — assigning it IS the act of putting somebody in
 * the leasing seat.
 *
 * ★ SO ASSIGNING THIS DEPARTMENT IS A PERMISSIONS DECISION, not a routing hint.
 * Elsewhere `primaryDepartment` only ever narrows CONTENT — which handoffs land
 * in a queue (`createHandoff`), who gets the rent-ready announcement
 * (`make-ready.service`), which alerts a person sees (`queue-feed`). Here it
 * also narrows ACCESS. Anyone tagged Leasing in `TeamPage` — whatever their
 * role — gets the trimmed shell from their next page load.
 *
 * ★ ORG_ADMIN IS THE ONE CARVE-OUT, and it is a safety valve rather than a
 * softening of the rule: `/team` is the only surface where a department can be
 * changed, so an admin who tagged themselves Leasing would be unable to reach
 * the page that undoes it. Recovering would need a second admin or an ops
 * session. Every non-admin role — PM_MANAGER, MAINTENANCE, VIEWER, ACCOUNTING —
 * is trimmed exactly as ruled.
 */
export function isLeasingSeat(user: LeasingSeatUser | null | undefined): boolean {
  if (!user) return false;
  // Ops sessions see the whole product (mirrors the ops bypass everywhere else).
  if (user.type === 'ops') return false;
  // The escape hatch: only `/team` can un-tag a department, and it is off the
  // allow-list. An admin who trapped themselves could not let themselves out.
  if (user.role === 'ORG_ADMIN') return false;
  return user.primaryDepartment === 'LEASING';
}

/**
 * Paths the leasing seat may open. Everything else redirects to My Queue.
 * Account settings stay reachable from the avatar menu.
 *
 * `search` is accepted for signature parity with `isAccountingAllowedPath` —
 * leasing has no query-gated destination today (accounting's `/notices?noticeId=`
 * has no counterpart here), and a caller must not have to know that.
 */
export function isLeasingAllowedPath(
  pathname: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  search = '',
): boolean {
  if (pathname === '/my-queue' || pathname.startsWith('/my-queue/')) return true;
  if (pathname === '/account') return true;

  // Their own task page — the unit, not the inspection.
  if (new RegExp(`^/leasing/turns/${UUID}$`, 'i').test(pathname)) return true;

  /**
   * The evidence, explicitly allowed (see the header). Detail paths only: the
   * bare id, the walk, and the campaign view. `/inspections` (the register),
   * `/inspections/templates*` and `/inspections/:id/create-work-order` stay
   * closed — a register is a browsing surface and raising a work order is
   * maintenance's decision, not leasing's.
   */
  if (new RegExp(`^/inspections/${UUID}(/walk|/campaign)?$`, 'i').test(pathname)) {
    return true;
  }
  // One work order. NOT `/work-orders` (the register) and NOT `/edit`.
  if (new RegExp(`^/work-orders/${UUID}$`, 'i').test(pathname)) return true;

  return false;
}
